import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { filter, Subscription, switchMap, takeWhile, timer, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { format, isToday, isValid, isYesterday, parseISO } from 'date-fns';
import { AppUrls } from '../../../../routing/app-urls';
import { ApplicationStore, ApplicationStoreType } from '../../../../core/store/application.store';
import { HotelGptStore, HotelGptStoreType } from '../../../../core/store/hotel-gpt.store';
import { HotelGptService } from '../../../../hotel-gpt/service/hotel-gpt.service';
import { AgentStatus } from '../../../../hotel-gpt/types/agent-request';
import { FrontendHotelOption } from '../../../../hotel-gpt/types/agent-request';
import { PptExportResponse, PptExportStatus } from '../../../../hotel-gpt/types/ppt-export-request';
import { SpinnerComponent } from '../../../../shared/components/spinner/spinner.component';
import { generateRandomId } from '../../../../shared/utils/unique-id-utils';
import { ConfirmationPopupModel } from '../../../../shared/components/popup/confirmation-popup/confirmation-popup.model';
import { LayoutStore, LayoutStoreType } from '../../../../core/store/layout.store';
import { Toast, ToastType } from '../../../../shared/types/toast';
import { FileUtils } from '../../../../shared/utils/file-utils';

@Component({
    selector: 'juyo-conversation-history',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpinnerComponent],
    styleUrl: 'conversation-history.scss',
    template: `
        @if (!collapsed()) {
            <div class="chat-history-section">
                <div class="chat-history-header">Chat History</div>
                <div class="search-bar">
                    <span class="material-symbols-outlined search-icon">search</span>
                    <input
                        class="search-input"
                        type="text"
                        placeholder="Search..."
                        [value]="searchQuery()"
                        (input)="searchQuery.set($any($event.target).value)"
                    />
                </div>
                @if (hotelGptStore.isLoading()) {
                    <juyo-spinner class="loading-spinner" [size]="16" />
                }
                <div class="conversation-list">
                    @for (conv of filteredConversations(); track conv.conversationId) {
                        <button
                            class="conversation-item"
                            [class.active]="conv.conversationId === hotelGptStore.activeConversationId()"
                            (click)="onConversationClick(conv.conversationId)"
                        >
                            <div class="conversation-top">
                                <span class="title">{{ conv.question }}</span>
                                <span class="date">{{ formatDate(conv.updatedAt) }}</span>
                            </div>
                            <div class="conversation-bottom">
                                <span class="hotels">
                                    @if (conv.hotels.length === 1) {
                                        {{ conv.hotels[0].name }}
                                    } @else if (conv.hotels.length > 1) {
                                        <span [title]="hotelNames(conv.hotels)"
                                            >{{ conv.hotels.length }} properties</span
                                        >
                                    }
                                </span>
                                @if (hotelGptStore.pendingConversations().has(conv.conversationId)) {
                                    <juyo-spinner class="pending-spinner" [size]="16" />
                                } @else {
                                    <button
                                        class="delete-btn"
                                        (click)="onDeleteConversation($event, conv.conversationId)"
                                        title="Delete conversation"
                                    >
                                        <span class="material-symbols-outlined">delete</span>
                                    </button>
                                }
                            </div>
                        </button>
                    }
                </div>
            </div>
        }
    `,
})
export class ConversationHistoryComponent {
    private router = inject(Router);
    private destroyRef = inject(DestroyRef);
    private applicationStore: ApplicationStoreType = inject(ApplicationStore);
    private layoutStore: LayoutStoreType = inject(LayoutStore);
    private hotelGptService = inject(HotelGptService);
    protected hotelGptStore: HotelGptStoreType = inject(HotelGptStore);

    public collapsed = input.required<boolean>();

    protected searchQuery = signal('');
    protected filteredConversations = computed(() => {
        const q = this.searchQuery().toLowerCase().trim();
        const conversations = this.hotelGptStore.conversations();
        if (!q) return conversations;
        return conversations.filter((c) => c.question.toLowerCase().includes(q));
    });

    private pollSubscriptions = new Map<string, Subscription>();
    private pptPollSubscription?: Subscription;

    constructor() {
        toObservable(this.applicationStore.selectedOrganization)
            .pipe(
                filter((organization) => !!organization),
                switchMap((organization) => {
                    this.hotelGptStore.setIsLoading(true);
                    return this.hotelGptService
                        .getConversations(organization!.id)
                        .pipe(catchError((error) => throwError(error))); // deprecated?
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((conversations) => {
                this.cancelPolling();
                this.hotelGptStore.setConversations(conversations);
                this.hotelGptStore.setIsLoading(false);
                this.hotelGptStore.startNewChat();
            });

        toObservable(this.hotelGptStore.pollJobs)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((jobs) => {
                const activeJobIds = new Set(jobs.map((j) => j.conversationId));
                for (const [convId, sub] of this.pollSubscriptions) {
                    if (!activeJobIds.has(convId)) {
                        sub.unsubscribe();
                        this.pollSubscriptions.delete(convId);
                    }
                }
                for (const job of jobs) {
                    if (!this.pollSubscriptions.has(job.conversationId)) {
                        this.startPoll(job.transactionId, job.conversationId);
                    }
                }
            });

        // a single batched poll covers all pending ppt exports (getPptStatus accepts multiple ids)
        toObservable(this.hotelGptStore.pptExportJobs)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((jobs) => {
                if (jobs.length === 0) {
                    this.pptPollSubscription?.unsubscribe();
                    this.pptPollSubscription = undefined;
                } else if (!this.pptPollSubscription) {
                    this.startPptPolling();
                }
            });
    }

    private startPptPolling(): void {
        this.pptPollSubscription = timer(3_000, 3_000)
            .pipe(
                switchMap(() => {
                    const transactionIds = this.hotelGptStore.pptExportJobs().map((j) => j.transactionId);
                    if (transactionIds.length === 0) return of<PptExportResponse[]>([]);
                    // keep polling on transient status errors rather than dropping every pending export
                    return this.hotelGptService.getPptStatus(transactionIds).pipe(catchError(() => of([])));
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((statuses) => this.handlePptStatuses(statuses));
    }

    private handlePptStatuses(statuses: PptExportResponse[]): void {
        for (const status of statuses) {
            const job = this.hotelGptStore.pptExportJobs().find((j) => j.transactionId === status.transactionId);
            if (!job) continue;

            if (status.status === PptExportStatus.Completed) {
                this.hotelGptStore.removePptExportJob(status.transactionId);
                this.markTurnExportCompleted(job.conversationId, job.turnId);
                this.downloadPpt(job.conversationId, job.turnId);
            } else if (status.status === PptExportStatus.Error) {
                this.hotelGptStore.removePptExportJob(status.transactionId);
                this.showPptError();
            }
        }
    }

    // once an export finishes, mark its turn completed so a later click downloads it directly
    private markTurnExportCompleted(conversationId: string, turnId: string): void {
        if (this.hotelGptStore.activeConversationId() !== conversationId) return;
        const message = this.hotelGptStore.activeMessages().find((m) => m.turnId === turnId);
        if (!message) return;
        this.hotelGptStore.updateActiveMessage(message.transactionId, {
            pptExportStatus: PptExportStatus.Completed,
        });
    }

    private downloadPpt(conversationId: string, turnId: string): void {
        this.hotelGptService.downloadPpt(conversationId, turnId).subscribe({
            next: ({ blob, fileName }) => new FileUtils().downloadFile(blob, fileName),
            error: () => this.showPptError(),
        });
    }

    private showPptError(): void {
        this.layoutStore.addTimedToast(
            new Toast(generateRandomId(), ['PowerPoint export failed. Please try again.'], ToastType.Error),
            4000,
        );
    }

    private startPoll(transactionId: string, conversationId: string): void {
        const sub = timer(3_000, 3_000)
            .pipe(
                switchMap(() => this.hotelGptService.getStatus(transactionId)),
                takeWhile((res) => res.status === AgentStatus.Pending, true),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (response) => {
                    if (response.status === AgentStatus.Pending) {
                        if (this.hotelGptStore.activeConversationId() === conversationId) {
                            this.hotelGptStore.updateActiveMessage(transactionId, { progressMsg: response.progressMsg });
                        }
                        return;
                    }

                    if (this.hotelGptStore.activeConversationId() === conversationId) {
                        this.hotelGptStore.updateActiveMessage(transactionId, {
                            status: response.status,
                            answer: response.answer,
                            options: response.options,
                            turnId: response.turnId,
                            userRating: 0,
                        });
                    }

                    this.hotelGptStore.updateConversationTimestamp(conversationId);
                    this.hotelGptStore.removePendingConversation(conversationId);
                    this.hotelGptStore.removePollJob(conversationId);
                    this.pollSubscriptions.delete(conversationId);
                },
                error: () => {
                    if (this.hotelGptStore.activeConversationId() === conversationId) {
                        this.hotelGptStore.updateActiveMessage(transactionId, { status: AgentStatus.Error });
                    }

                    this.hotelGptStore.removePendingConversation(conversationId);
                    this.hotelGptStore.removePollJob(conversationId);
                    this.pollSubscriptions.delete(conversationId);
                },
            });

        this.pollSubscriptions.set(conversationId, sub);
    }

    private cancelPolling(): void {
        this.pollSubscriptions.forEach((sub) => sub.unsubscribe());
        this.pollSubscriptions.clear();
        this.pptPollSubscription?.unsubscribe();
        this.pptPollSubscription = undefined;
        this.hotelGptStore.clearPollJobs();
        this.hotelGptStore.clearPptExportJobs();
        this.hotelGptStore.clearPendingConversations();
    }

    protected onConversationClick(conversationId: string): void {
        this.hotelGptStore.selectConversation(conversationId);
        this.router.navigate(['/', AppUrls.hotelGptUrl]);
    }

    protected onDeleteConversation(event: MouseEvent, conversationId: string): void {
        event.stopPropagation();

        this.layoutStore.showPopup(
            new ConfirmationPopupModel(
                generateRandomId(),
                'Delete conversation',
                'This conversation will be permanently deleted and cannot be undone.',
                () => {
                    this.hotelGptService.deleteConversation(conversationId).subscribe(() => {
                        this.hotelGptStore.removeConversation(conversationId);
                        if (this.hotelGptStore.activeConversationId() === conversationId) {
                            this.hotelGptStore.startNewChat();
                        }
                    });
                },
            ),
        );
    }

    protected hotelNames(hotels: FrontendHotelOption[]): string {
        return hotels.map((h) => h.name).join(', ');
    }

    protected formatDate(dateStr: string): string {
        if (!dateStr) {
            return '';
        }
        const date = parseISO(dateStr);
        if (!isValid(date)) {
            return dateStr;
        } else if (isToday(date)) {
            return 'Today';
        } else if (isYesterday(date)) {
            return 'Yesterday';
        } else {
            return format(date, 'MMM d');
        }
    }
}
